import { notifications } from '@mantine/notifications'
import { observable } from 'mobx'
import { getSnapshot, type Instance, type SnapshotIn, types } from 'mobx-state-tree'

import { bulkUploadToasts } from '@/screens/BulkContentUpload/bulkUploadJobCopy'
import { withEnv } from '@/services/state/helpers/withEnv'
import { withRootStore } from '@/services/state/helpers/withRootStore'
import * as commonClasses from '@/styles/componentCommons.css'
import type { BulkUploadMode, ConfirmedFile } from '@/utils/bulkContentUpload'
import {
  AbortedError,
  ChannelChangedError,
  commitModule,
  commitStep,
  isAbort,
  isModuleTaken,
  removeModule,
  removeStep,
  uploadFile,
} from '@/utils/bulkUploadTask'
import * as storage from '@/utils/storage'

/** Her spec asks for 3–4 (decision 14); large videos compete for the PM's bandwidth, so the low end. */
export const UPLOAD_CONCURRENCY = 3
/**
 * Files processing at once. Backend has no capacity concern (2026-09-25); this bounds the job's own
 * polling, since each processing file checks its status every `PROCESSING_POLL_MS`.
 */
export const PROCESSING_CONCURRENCY = 10
/** Decision 44: progress reaches MST about four times a second, not once per event. */
export const PROGRESS_FLUSH_MS = 250
const LIBRARY_REFRESH_MS = 3000
const EXIT_SETTLE_MS = 10_000

/**
 * One record per channel: a job in one channel must not overwrite what another channel's
 * interrupted job left to clean up.
 */
export const BULK_UPLOAD_RECORD_PREFIX = 'sme-portal-bulk-upload:'
export const bulkUploadRecordKey = (sponsorId: string) => BULK_UPLOAD_RECORD_PREFIX + sponsorId
/**
 * Held by the tab running a job for as long as it has work in flight. The browser releases it when
 * the tab closes or crashes, which is how another tab tells a live job from an interrupted one.
 */
export const BULK_UPLOAD_LOCK = 'sme-portal-bulk-upload'

const TAB_ID = crypto.randomUUID()

export type BulkFileStatus = 'queued' | 'uploading' | 'processing' | 'complete' | 'failed' | 'interrupted'
export type BulkJobState = 'running' | 'finished' | 'interrupted'

const FILE_STATUSES: BulkFileStatus[] = ['queued', 'uploading', 'processing', 'complete', 'failed', 'interrupted']
const IN_PROGRESS: BulkFileStatus[] = ['queued', 'uploading', 'processing']
/** Decision 16: a file can be removed until its bytes have landed. */
export const REMOVABLE: BulkFileStatus[] = ['queued', 'uploading', 'failed']

const BulkUploadFileModel = types.model('BulkUploadFile', {
  name: types.string,
  size: types.number,
  fileType: types.string,
  title: types.string,
  eskId: types.string,
  deliveryModeName: types.maybeNull(types.string),
  status: types.enumeration<BulkFileStatus>(FILE_STATUSES),
  progress: 0,
  /**
   * Processing's own percentage, the highest the backend has reported so it never goes backwards; null
   * until it reports one, and for the types it never reports one for.
   */
  processed: types.maybeNull(types.number),
})

export interface BulkUploadFile extends Instance<typeof BulkUploadFileModel> {}

const BulkUploadJobModel = types
  .model('BulkUploadJob', {
    id: types.string,
    mode: types.enumeration<BulkUploadMode>(['modules', 'supplementary']),
    /** The module a supplementary job adds to. */
    taskId: types.maybe(types.string),
    moduleTitle: types.maybe(types.string),
    state: types.enumeration<BulkJobState>(['running', 'finished', 'interrupted']),
    files: types.array(BulkUploadFileModel),
  })
  .views(self => ({
    countOf(...statuses: BulkFileStatus[]) {
      return self.files.filter(f => statuses.includes(f.status)).length
    },
  }))
  .views(self => ({
    get total() {
      return self.files.length
    },
    get completed() {
      return self.countOf('complete')
    },
    get failed() {
      return self.countOf('failed')
    },
    /** "{n} files still waiting" in the Stop and Close confirmations. */
    get waiting() {
      return self.countOf('queued', 'uploading')
    },
    get didNotUpload() {
      return self.countOf('interrupted')
    },
    /**
     * Each file in play counts as half upload and half processing, so 100% means every module is
     * created, not just every byte sent. A failed file drops out rather than holding it back.
     */
    get percent() {
      let files = 0
      let done = 0
      for (const f of self.files) {
        if (f.status === 'failed' || f.status === 'interrupted') continue
        files++
        if (f.status === 'uploading') done += f.progress / 200
        else if (f.status === 'processing') done += 0.5 + (f.processed ?? 0) / 200
        else if (f.status === 'complete') done += 1
      }
      // Floored, so the last file still processing never rounds up to 100.
      return files === 0 ? 0 : Math.floor((done * 100) / files)
    },
  }))

export interface BulkUploadJob extends Instance<typeof BulkUploadJobModel> {}
type JobSnapshot = SnapshotIn<typeof BulkUploadJobModel>

export type UnfinishedStep = { taskId: string; stepId: string }

/** Decision 50: what a returning tab needs to show the summary and remove half-built modules. */
export type BulkUploadRecord = {
  tabId: string
  userId: string
  sponsorId: string
  /** The writing tab still had work in flight. */
  running: boolean
  job?: JobSnapshot
  unfinishedTaskIds: string[]
  unfinishedSteps?: UnfinishedStep[]
}

/** Worth waiting for the lock over: a live job, a summary to show, or modules to remove. */
const hasSomethingToRestore = (record: BulkUploadRecord | undefined) =>
  !!record &&
  (record.running ||
    record.job?.state === 'interrupted' ||
    record.unfinishedTaskIds.length > 0 ||
    !!record.unfinishedSteps?.length)

/** Who in this tab is relying on a module's edit lock (decision 46). */
type LockHolder = 'editor' | 'job'

const locksSupported = () => typeof navigator !== 'undefined' && !!navigator.locks

export const BulkUploadStoreModel = types
  .model('BulkUploadStore')
  .props({
    job: types.maybe(BulkUploadJobModel),
    /** Modules a job created and hasn't finished — removed if the job can't finish them (32). */
    unfinishedTaskIds: types.array(types.string),
    /** The same for files a supplementary job added to its module. */
    unfinishedSteps: types.array(types.frozen<UnfinishedStep>()),
    panelOpen: false,
  })
  .extend(withRootStore)
  .extend(withEnv)
  .volatile(() => ({
    inFlight: 0,
    otherTabBusy: false,
    /** A record is waiting on a lock someone else holds, which may be a page still closing. */
    restorePending: false,
    /** The PM already agreed to leave through one of our own warnings; the browser's would ask twice. */
    leaveConfirmed: false,
    /** Outlives the job while a stopped one commits its last files, for the copy that warns about it. */
    activeMode: undefined as BulkUploadMode | undefined,
    otherTabMode: undefined as BulkUploadMode | undefined,
    /** Modules a job in this tab holds the lock on, observable so Edit CM can react to them. */
    jobHeldModules: [] as string[],
    /**
     * Modules the job created and is still processing, which the library shows as processing. A map, so
     * each library row tracks only its own module's entry.
     */
    creatingModules: observable.map<string, true>(),
  }))
  .views(self => ({
    get isRunning() {
      return self.job?.state === 'running'
    },
    /** Leaving now would lose work: a running job, or a stopped one still committing its last files. */
    get isBusy() {
      return self.job?.state === 'running' || self.inFlight > 0
    },
    get isStartBlocked() {
      return self.job?.state === 'running' || self.otherTabBusy
    },
    /** Save sends `lock: false`, and Edit CM turns off drag-reorder, while this is true (46). */
    jobHoldsModule(taskId: string): boolean {
      return self.jobHeldModules.includes(taskId)
    },
    /** The module is a draft already, but its file is still processing. */
    isCreatingModule(taskId: string): boolean {
      return self.creatingModules.has(taskId)
    },
  }))
  .views(self => ({
    /** A job in this tab or another: logging out or switching channel here would still end it. */
    get isAnyJobBusy() {
      return self.isBusy || self.otherTabBusy
    },
    /** Which job the leave warnings are about; undefined when another tab's record doesn't say. */
    get busyMode(): BulkUploadMode | undefined {
      if (self.isBusy) return self.job?.mode ?? self.activeMode
      return self.otherTabBusy ? self.otherTabMode : undefined
    },
  }))
  .actions(self => ({
    setPanelOpen(open: boolean) {
      self.panelOpen = open
    },
    _setJob(job: JobSnapshot | undefined) {
      self.job = job ? BulkUploadJobModel.create(job) : undefined
      if (!job) self.panelOpen = false
    },
    _setJobState(state: BulkJobState) {
      if (self.job) self.job.state = state
    },
    _patchFile(jobId: string, name: string, patch: Partial<Pick<BulkUploadFile, 'status' | 'progress' | 'processed'>>) {
      if (self.job?.id !== jobId) return
      const row = self.job.files.find(f => f.name === name)
      if (row) Object.assign(row, patch)
    },
    _removeFile(jobId: string, name: string) {
      if (self.job?.id !== jobId) return
      const row = self.job.files.find(f => f.name === name)
      if (row) self.job.files.remove(row)
    },
    _applyProgress(updates: Array<{ jobId: string; name: string; percent: number }>) {
      for (const { jobId, name, percent } of updates) {
        if (self.job?.id !== jobId) continue
        const row = self.job.files.find(f => f.name === name)
        if (row?.status === 'uploading') row.progress = percent
      }
    },
    _addUnfinished(taskId: string) {
      self.unfinishedTaskIds.push(taskId)
    },
    _dropUnfinished(taskId: string) {
      self.unfinishedTaskIds.remove(taskId)
    },
    _setUnfinished(taskIds: string[]) {
      self.unfinishedTaskIds.replace(taskIds)
    },
    _addUnfinishedStep(step: UnfinishedStep) {
      self.unfinishedSteps.push(step)
    },
    _dropUnfinishedStep(stepId: string) {
      self.unfinishedSteps.replace(self.unfinishedSteps.filter(s => s.stepId !== stepId))
    },
    _setUnfinishedSteps(steps: UnfinishedStep[]) {
      self.unfinishedSteps.replace(steps)
    },
    _setActiveMode(mode: BulkUploadMode | undefined) {
      self.activeMode = mode
    },
    _setJobHeldModules(taskIds: string[]) {
      self.jobHeldModules = taskIds
    },
    _clearCreating() {
      self.creatingModules.clear()
    },
    _setCreating(taskId: string, creating: boolean) {
      if (creating) self.creatingModules.set(taskId, true)
      else self.creatingModules.delete(taskId)
    },

    _changeInFlight(delta: number) {
      self.inFlight += delta
    },
    /** The mode is whatever the other tab's record says, and is dropped with the flag so it can't go stale. */
    _setOtherTabBusy(busy: boolean, mode?: BulkUploadMode) {
      self.otherTabBusy = busy
      self.otherTabMode = busy ? mode : undefined
    },
    _setRestorePending(pending: boolean) {
      self.restorePending = pending
    },
    confirmLeave() {
      self.leaveConfirmed = true
    },
  }))
  .actions(self => {
    const files = new Map<string, File>()
    const controllers = new Map<string, AbortController>()
    const pendingProgress = new Map<string, { jobId: string; name: string; percent: number }>()
    const commitWaiters: Array<() => void> = []
    let progressTimer: ReturnType<typeof setInterval> | undefined
    let refreshTimer: ReturnType<typeof setTimeout> | undefined
    let lastRefresh = 0
    let listToRefresh = false
    const stepsToRefresh = new Set<string>()
    let activeUploads = 0
    let activeCommits = 0
    let lockRelease: (() => void) | undefined
    let cleaning = false
    let restoring = false
    /** Bumped by every write of the other-tab flag, so a lock check that started earlier can't overwrite it. */
    let otherTabChecks = 0
    /** The record belongs to the channel and user the job started in, not whoever is signed in now. */
    let owner: { userId: string; sponsorId: string } | undefined
    /**
     * Decision 46: the editor and the job share one server-side lock for the same user, so whichever
     * of them lets go first leaves it in place, and only the last one to need it unlocks.
     */
    const lockHolders = new Map<string, Set<LockHolder>>()
    /**
     * Modules supplementary jobs hold the lock on for their run (31). A stopped job's module stays held
     * until its last processing file settles, even once another job has started on a different module.
     */
    const jobModules = new Set<string>()
    const moduleInFlight = new Map<string, number>()
    /** Steps and modules a file is still committing, or removing after it failed. Retry leaves them to it. */
    const committing = new Set<string>()

    const keyOf = (jobId: string, name: string) => `${jobId}/${name}`

    /** Shared, so a start and a restore racing in this tab don't read each other as another tab. */
    let lockRequest: Promise<boolean> | undefined

    function acquireLock(): Promise<boolean> {
      if (lockRelease || !locksSupported()) return Promise.resolve(true)
      lockRequest ??= new Promise<boolean>(resolve => {
        void navigator.locks.request(BULK_UPLOAD_LOCK, { ifAvailable: true }, lock => {
          if (!lock) {
            resolve(false)
            return
          }
          return new Promise<void>(release => {
            lockRelease = release
            resolve(true)
          })
        })
      }).finally(() => {
        lockRequest = undefined
      })
      return lockRequest
    }

    /** For a tab with nothing of its own to restore, the lock is the only sign another tab is busy. */
    async function lockHeldElsewhere(): Promise<boolean> {
      if (lockRelease || !locksSupported()) return false
      const { held = [] } = await navigator.locks.query()
      return held.some(lock => lock.name === BULK_UPLOAD_LOCK)
    }

    function markOtherTab(busy: boolean, mode?: BulkUploadMode) {
      otherTabChecks++
      self._setOtherTabBusy(busy, mode)
    }

    async function recheckOtherTab(mode?: BulkUploadMode) {
      const check = ++otherTabChecks
      const busy = await lockHeldElsewhere()
      if (check === otherTabChecks) self._setOtherTabBusy(busy, mode)
    }

    function maybeReleaseLock() {
      if (self.isBusy || cleaning || !lockRelease) return
      lockRelease()
      lockRelease = undefined
    }

    const holdsRecord = () => !!lockRelease || !locksSupported()

    function syncJobHeldModules() {
      self._setJobHeldModules([...lockHolders].filter(([, holders]) => holders.has('job')).map(([taskId]) => taskId))
    }

    function holdModule(taskId: string, holder: LockHolder) {
      const holders = lockHolders.get(taskId) ?? new Set<LockHolder>()
      holders.add(holder)
      lockHolders.set(taskId, holders)
      syncJobHeldModules()
    }

    /** True when nobody in this tab needs the lock any more, so the server's should be released. */
    function releaseModule(taskId: string, holder: LockHolder): boolean {
      const holders = lockHolders.get(taskId)
      if (!holders?.delete(holder)) return false
      syncJobHeldModules()
      if (holders.size > 0) return false
      lockHolders.delete(taskId)
      return true
    }

    const holds = (taskId: string, holder: LockHolder) => !!lockHolders.get(taskId)?.has(holder)

    const unlock = (taskId: string) =>
      self.api.unlockContentModule({ task_id: taskId }).catch(e => console.warn('[BulkUploadStore] unlock failed', e))

    // Edit CM's own `pagehide` unlock exists only while it is on screen.
    const unlockOnPageHide = () => {
      for (const taskId of jobModules) self.api.unlockContentModuleOnPageHide(taskId)
    }

    /** Takes the module's lock for the job's run: `taken` when a co-owner has it, `failed` for anything else. */
    async function takeModule(taskId: string): Promise<'held' | 'taken' | 'failed'> {
      try {
        await self.api.lockContentModule({ task_id: taskId })
      } catch (error) {
        console.error(`[BulkUploadStore] could not lock ${taskId}`, error)
        return isModuleTaken(error) ? 'taken' : 'failed'
      }
      if (!jobModules.has(taskId)) {
        if (jobModules.size === 0) window.addEventListener('pagehide', unlockOnPageHide)
        jobModules.add(taskId)
        holdModule(taskId, 'job')
      }
      return 'held'
    }

    function letGoOfModule(taskId: string, options = { unlock: true }) {
      if (!jobModules.delete(taskId)) return
      if (jobModules.size === 0) window.removeEventListener('pagehide', unlockOnPageHide)
      if (releaseModule(taskId, 'job') && options.unlock) void unlock(taskId)
    }

    function letGoOfAllModules(options = { unlock: true }) {
      for (const taskId of [...jobModules]) letGoOfModule(taskId, options)
    }

    /** A module is let go once no running job targets it and none of its files are still committing. */
    function maybeLetGoOfModules() {
      if (cleaning) return
      for (const taskId of [...jobModules]) {
        const targeted = self.job?.state === 'running' && self.job.taskId === taskId
        if (!targeted && !moduleInFlight.get(taskId)) letGoOfModule(taskId)
      }
    }

    function persist() {
      if (!owner || !holdsRecord()) return
      if (!self.job && self.unfinishedTaskIds.length === 0 && self.unfinishedSteps.length === 0 && !self.isBusy) {
        // Holding the lock means no live tab owns the record, whichever tab wrote it.
        storage.remove(bulkUploadRecordKey(owner.sponsorId))
        return
      }
      const record: BulkUploadRecord = {
        tabId: TAB_ID,
        ...owner,
        running: self.isBusy,
        job: self.job && {
          id: self.job.id,
          mode: self.job.mode,
          taskId: self.job.taskId,
          moduleTitle: self.job.moduleTitle,
          state: self.job.state,
          files: self.job.files.map(f => ({ ...getSnapshot(f), progress: 0, processed: null })),
        },
        unfinishedTaskIds: [...self.unfinishedTaskIds],
        unfinishedSteps: [...self.unfinishedSteps],
      }
      storage.save(bulkUploadRecordKey(owner.sponsorId), record)
    }

    /** Done and Dismiss end what the PM sees, but modules still to be removed keep the record alive. */
    function clearRecord() {
      if (!owner) return
      const key = bulkUploadRecordKey(owner.sponsorId)
      const record = storage.load<BulkUploadRecord>(key)
      if (record && record.tabId !== TAB_ID && !holdsRecord()) return
      if (self.unfinishedTaskIds.length > 0 || self.unfinishedSteps.length > 0) {
        const record: BulkUploadRecord = {
          tabId: TAB_ID,
          ...owner,
          running: false,
          unfinishedTaskIds: [...self.unfinishedTaskIds],
          unfinishedSteps: [...self.unfinishedSteps],
        }
        storage.save(key, record)
      } else {
        storage.remove(key)
      }
    }

    function toast(message: string, color: 'green' | 'red' | 'gray' = 'green') {
      notifications.show({ message, color, classNames: commonClasses.notificationFilled, position: 'bottom-left' })
    }

    function toastNotHeld(result: 'taken' | 'failed') {
      const { moduleLocked, lockFailed } = bulkUploadToasts.supplementary
      toast(result === 'taken' ? moduleLocked : lockFailed, 'red')
    }

    function scheduleRefresh() {
      if (refreshTimer) return
      const wait = Math.max(0, lastRefresh + LIBRARY_REFRESH_MS - Date.now())
      refreshTimer = setTimeout(() => {
        refreshTimer = undefined
        lastRefresh = Date.now()
        if (listToRefresh) void self.api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
        for (const taskId of stepsToRefresh)
          void self.api.queryClient.invalidateQueries({ queryKey: ['steps', taskId] })
        listToRefresh = false
        stepsToRefresh.clear()
      }, wait)
    }

    function refreshLibrary() {
      listToRefresh = true
      scheduleRefresh()
    }

    /**
     * An open editor's file list, so files a job adds appear as they land (46). Not the library's: nothing
     * in its rows changes when a file is added, and Edit CM keeps the whole module list loaded.
     */
    function refreshSteps(taskId: string) {
      stepsToRefresh.add(taskId)
      scheduleRefresh()
    }

    function flushProgress() {
      if (pendingProgress.size === 0) {
        clearInterval(progressTimer)
        progressTimer = undefined
        return
      }
      self._applyProgress([...pendingProgress.values()])
      pendingProgress.clear()
    }

    function queueProgress(jobId: string, name: string, percent: number) {
      pendingProgress.set(keyOf(jobId, name), { jobId, name, percent })
      progressTimer ??= setInterval(flushProgress, PROGRESS_FLUSH_MS)
    }

    function acquireCommitSlot(signal: AbortSignal): Promise<void> {
      if (activeCommits < PROCESSING_CONCURRENCY) {
        activeCommits++
        return Promise.resolve()
      }
      return new Promise((resolve, reject) => {
        const waiter = () => {
          signal.removeEventListener('abort', onAbort)
          activeCommits++
          resolve()
        }
        const onAbort = () => {
          commitWaiters.splice(commitWaiters.indexOf(waiter), 1)
          reject(new AbortedError())
        }
        commitWaiters.push(waiter)
        signal.addEventListener('abort', onAbort, { once: true })
      })
    }

    function releaseCommitSlot() {
      activeCommits--
      commitWaiters.shift()?.()
    }

    function guardChannel(sponsorId: string) {
      if (self.rootStore.authenticationStore.user?.sponsorId === sponsorId) return
      stopForChannelChange()
      throw new ChannelChangedError()
    }

    function settle() {
      const job = self.job
      if (job?.state === 'running' && job.countOf(...IN_PROGRESS) === 0) self._setJobState('finished')
      persist()
      maybeReleaseLock()
      maybeLetGoOfModules()
    }

    function clearJob() {
      if (self.job) {
        for (const f of self.job.files) files.delete(keyOf(self.job.id, f.name))
      }
      self._setJob(undefined)
    }

    function abortAll() {
      for (const controller of controllers.values()) controller.abort()
    }

    /** Decision 31: once a co-owner has the module, files that haven't landed fail rather than upload. */
    function failRemaining(jobId: string) {
      const job = self.job
      if (job?.id !== jobId) return
      const remaining = job.files.filter(f => f.status === 'queued' || f.status === 'uploading')
      if (remaining.length === 0) return
      for (const f of remaining) {
        controllers.get(keyOf(jobId, f.name))?.abort()
        self._patchFile(jobId, f.name, { status: 'failed', progress: 0 })
      }
      toast(bulkUploadToasts.supplementary.moduleTaken, 'red')
    }

    function pump() {
      const job = self.job
      if (job?.state !== 'running') return
      while (activeUploads < UPLOAD_CONCURRENCY) {
        const next = job.files.find(f => f.status === 'queued')
        if (!next) return
        activeUploads++
        self._patchFile(job.id, next.name, { status: 'uploading', progress: 0 })
        void runFile(job.id, next.name)
      }
    }

    async function runFile(jobId: string, name: string) {
      const key = keyOf(jobId, name)
      const file = files.get(key)
      const row = self.job?.files.find(f => f.name === name)
      const moduleTaskId = self.job?.mode === 'supplementary' ? self.job.taskId : undefined
      const sponsorId = owner?.sponsorId
      const controller = new AbortController()
      controllers.set(key, controller)
      self._changeInFlight(1)
      if (moduleTaskId) moduleInFlight.set(moduleTaskId, (moduleInFlight.get(moduleTaskId) ?? 0) + 1)
      let taskId: string | undefined
      let stepId: string | undefined
      let holdsUploadSlot = true
      const releaseUploadSlot = () => {
        if (!holdsUploadSlot) return
        holdsUploadSlot = false
        pendingProgress.delete(key)
        activeUploads--
        pump()
      }

      try {
        if (!file || !row || !sponsorId) throw new Error(`Nothing staged for ${name}`)
        const commit = { fileName: name, title: row.title, eskId: row.eskId, deliveryModeName: row.deliveryModeName }

        let filePath: string
        try {
          filePath = await uploadFile(self.api, file, controller.signal, percent => queueProgress(jobId, name, percent))
        } finally {
          releaseUploadSlot()
        }
        self._patchFile(jobId, name, { status: 'processing', progress: 100, processed: null })
        let peak = 0
        const onProcessing = (percent: number) => {
          peak = Math.max(peak, percent)
          self._patchFile(jobId, name, { processed: peak })
        }

        await acquireCommitSlot(controller.signal)
        try {
          if (moduleTaskId) {
            const result = await commitStep(
              self.api,
              { taskId: moduleTaskId, filePath, fileName: name, title: commit.title },
              controller.signal,
              {
                guard: () => guardChannel(sponsorId),
                onProcessing,
                onStepCreated: id => {
                  stepId = id
                  committing.add(id)
                  self._addUnfinishedStep({ taskId: moduleTaskId, stepId: id })
                  persist()
                  refreshSteps(moduleTaskId)
                },
              },
            )
            self._dropUnfinishedStep(result.stepId)
          } else {
            const result = await commitModule(self.api, { ...commit, filePath }, controller.signal, {
              guard: () => guardChannel(sponsorId),
              onProcessing,
              onModuleCreated: id => {
                taskId = id
                committing.add(id)
                self._setCreating(id, true)
                // `/cm/create` locked it to us; an editor opened on the draft mustn't unlock it under the job.
                holdModule(id, 'job')
                self._addUnfinished(id)
                // Written now, not when the file settles, or a tab closed mid-processing strands it.
                persist()
              },
              editorHolds: id => holds(id, 'editor'),
            })
            self._dropUnfinished(result.taskId)
          }
          self._patchFile(jobId, name, { status: 'complete' })
          if (moduleTaskId) refreshSteps(moduleTaskId)
          else refreshLibrary()
        } finally {
          releaseCommitSlot()
          // The final update released the lock or left it to the editor, so there is nothing to unlock.
          if (taskId) releaseModule(taskId, 'job')
        }
      } catch (error) {
        // Whoever aborted has already dealt with the row; a module it left stays unfinished for cleanup.
        if (isAbort(error, controller.signal) || error instanceof ChannelChangedError) return
        console.error(`[BulkUploadStore] ${name} failed`, error)
        self._patchFile(jobId, name, { status: 'failed', progress: 0 })
        persist()
        if (moduleTaskId && isModuleTaken(error)) failRemaining(jobId)
        if (taskId) {
          // Failed in the panel already, so not processing in the library while the removal is confirmed.
          self._setCreating(taskId, false)
          if (await removeModule(self.api, taskId)) self._dropUnfinished(taskId)
          refreshLibrary()
        }
        if (moduleTaskId && stepId) {
          if (await removeStep(self.api, moduleTaskId, stepId)) self._dropUnfinishedStep(stepId)
          refreshSteps(moduleTaskId)
        }
      } finally {
        releaseUploadSlot()
        if (stepId) committing.delete(stepId)
        if (taskId) {
          committing.delete(taskId)
          self._setCreating(taskId, false)
        }
        if (controllers.get(key) === controller) controllers.delete(key)
        self._changeInFlight(-1)
        if (moduleTaskId) moduleInFlight.set(moduleTaskId, (moduleInFlight.get(moduleTaskId) ?? 1) - 1)
        settle()
      }
    }

    /** `removeStep` takes the lock when nobody has it; this hands it back unless the editor still needs it. */
    async function removeUnfinishedSteps(taskId: string, stepIds: string[]) {
      const alreadyHeld = holds(taskId, 'job')
      if (!alreadyHeld) holdModule(taskId, 'job')
      try {
        for (const stepId of stepIds) {
          if (await removeStep(self.api, taskId, stepId)) self._dropUnfinishedStep(stepId)
        }
      } finally {
        if (!alreadyHeld && releaseModule(taskId, 'job')) await unlock(taskId)
      }
      refreshSteps(taskId)
    }

    /** `leaveCommitting` keeps what a running file still owns, for a Retry while the job runs. */
    async function removeUnfinished(options: { confirm: boolean; leaveCommitting?: boolean }) {
      const removable = (id: string) => !options.leaveCommitting || !committing.has(id)
      const taskIds = self.unfinishedTaskIds.filter(removable)
      const stepsByModule = new Map<string, string[]>()
      for (const { taskId, stepId } of self.unfinishedSteps) {
        if (removable(stepId)) stepsByModule.set(taskId, [...(stepsByModule.get(taskId) ?? []), stepId])
      }
      cleaning = true
      const removesModules = taskIds.length > 0
      try {
        await Promise.all([
          ...taskIds.map(async taskId => {
            if (await removeModule(self.api, taskId, options)) self._dropUnfinished(taskId)
          }),
          ...[...stepsByModule].map(([taskId, stepIds]) => removeUnfinishedSteps(taskId, stepIds)),
        ])
        if (removesModules) refreshLibrary()
      } finally {
        cleaning = false
      }
    }

    async function restoreFrom(record: BulkUploadRecord, user: { userId: string; sponsorId: string }) {
      if (record.userId !== user.userId) {
        storage.remove(bulkUploadRecordKey(user.sponsorId))
        return
      }

      owner = { userId: record.userId, sponsorId: record.sponsorId }
      if (record.job && (record.running || record.job.state === 'interrupted')) {
        self._setJob({
          ...record.job,
          state: 'interrupted',
          files: (record.job.files ?? []).map(f => ({
            ...f,
            status: f.status === 'complete' || f.status === 'failed' ? f.status : 'interrupted',
            progress: 0,
          })),
        })
      }
      self._setUnfinished(record.unfinishedTaskIds)
      self._setUnfinishedSteps(record.unfinishedSteps ?? [])
      persist()
      await removeUnfinished({ confirm: true })
      persist()
    }

    function stopForChannelChange() {
      if (!self.isBusy) return
      abortAll()
      clearJob()
      // The server URLs may already point at the new channel; cleanup on return unlocks it there (50).
      letGoOfAllModules({ unlock: false })
      persist()
    }

    return {
      /**
       * Confirm on the wizard's review step; a `target` module makes it a supplementary job. Returns
       * false when another tab's job is still running, or a co-owner has the module.
       */
      async start(staged: ConfirmedFile[], target?: { taskId: string; moduleTitle?: string }): Promise<boolean> {
        const user = self.rootStore.authenticationStore.user
        if (self.isRunning || !user?.sponsorId || staged.length === 0) return false
        if (!(await acquireLock())) {
          markOtherTab(true)
          toast(bulkUploadToasts.otherTab, 'red')
          return false
        }
        if (target) {
          const taken = await takeModule(target.taskId)
          if (taken !== 'held') {
            maybeReleaseLock()
            toastNotHeld(taken)
            return false
          }
        }
        const mode: BulkUploadMode = target ? 'supplementary' : 'modules'

        clearJob()
        owner = { userId: user.userId, sponsorId: user.sponsorId }
        const id = crypto.randomUUID()
        for (const s of staged) {
          // A new File over the same bytes, so the PUT's Content-Type matches what was signed.
          const file = s.file.type ? s.file : new File([s.file], s.file.name, { type: s.mimeType })
          files.set(keyOf(id, s.file.name), file)
        }
        self._setActiveMode(mode)
        self._setJob({
          id,
          mode,
          taskId: target?.taskId,
          moduleTitle: target?.moduleTitle,
          state: 'running',
          files: staged.map(s => ({
            name: s.file.name,
            size: s.file.size,
            fileType: s.type,
            title: s.title,
            eskId: s.eskId,
            deliveryModeName: s.deliveryModeName,
            status: 'queued',
          })),
        })
        self.setPanelOpen(true)
        persist()
        pump()
        return true
      },

      cancelFile(name: string) {
        const job = self.job
        const row = job?.files.find(f => f.name === name)
        if (!job || !row || !REMOVABLE.includes(row.status)) return
        const key = keyOf(job.id, name)
        controllers.get(key)?.abort()
        files.delete(key)
        self._removeFile(job.id, name)
        if (job.files.length === 0) {
          toast(bulkUploadToasts[job.mode].cancelled, 'gray')
          clearJob()
        }
        settle()
        pump()
      },

      /** Files still uploading are cancelled; files already processing finish their commit. */
      stop() {
        const job = self.job
        // A confirmation opened while the job ran can land after it finished; Stop has nothing to do then.
        if (job?.state !== 'running') return
        for (const f of job.files) {
          if (f.status === 'queued' || f.status === 'uploading') controllers.get(keyOf(job.id, f.name))?.abort()
        }
        toast(bulkUploadToasts[job.mode].stopped(job.completed, job.countOf('processing')), 'gray')
        clearJob()
        settle()
      },

      /** Her Retry: every failed file goes back in the queue, whether or not the rest have finished. */
      async retryFailed() {
        const job = self.job
        if (!job || job.state === 'interrupted' || job.failed === 0) return
        if (!(await acquireLock())) {
          toast(bulkUploadToasts.otherTab, 'red')
          return
        }
        const stale = () =>
          self.job !== job ||
          job.failed === 0 ||
          owner?.sponsorId !== self.rootStore.authenticationStore.user?.sponsorId
        // Done or Dismiss can land while the lock is being asked for.
        if (stale()) {
          maybeReleaseLock()
          return
        }
        if (job.mode === 'supplementary' && job.taskId) {
          const taken = await takeModule(job.taskId)
          if (taken !== 'held') {
            maybeReleaseLock()
            toastNotHeld(taken)
            return
          }
          // Files a co-owner's lock kept the job from removing; files still processing are their own.
          if (self.unfinishedSteps.length > 0) await removeUnfinished({ confirm: true, leaveCommitting: true })
          if (stale()) {
            maybeReleaseLock()
            maybeLetGoOfModules()
            return
          }
        }
        for (const f of job.files) {
          if (f.status === 'failed') self._patchFile(job.id, f.name, { status: 'queued', progress: 0 })
        }
        self._setJobState('running')
        persist()
        pump()
      },

      /** Done on the finished panel. */
      done() {
        const job = self.job
        if (!job) return
        toast(bulkUploadToasts[job.mode].done(job.completed))
        clearJob()
        settle()
        clearRecord()
      },

      /** X on a finished banner, Dismiss on an interrupted one. */
      dismiss() {
        clearJob()
        settle()
        clearRecord()
      },

      /**
       * On load: a record whose tab no longer holds the lock was interrupted. Show what happened and
       * remove the modules it left half-built (decision 50). A live lock means another tab owns it.
       */
      async restore() {
        const user = self.rootStore.authenticationStore.user
        if (!user?.sponsorId || self.isBusy || restoring || !locksSupported()) return
        // A finished or interrupted job belongs to the channel it ran in; its record stays there.
        if (owner && owner.sponsorId !== user.sponsorId) {
          clearJob()
          self._setUnfinished([])
          self._setUnfinishedSteps([])
          owner = undefined
        }
        const key = bulkUploadRecordKey(user.sponsorId)
        restoring = true
        try {
          if (!storage.load(key)) {
            self._setRestorePending(false)
            await recheckOtherTab()
            return
          }
          if (!(await acquireLock())) {
            const waiting = storage.load<BulkUploadRecord>(key)
            markOtherTab(true, waiting?.job?.mode)
            self._setRestorePending(hasSomethingToRestore(waiting))
            return
          }
          markOtherTab(false)
          self._setRestorePending(false)
          // Read only once the lock is held, and leave alone a record this tab wrote: a start that
          // shared the lock request may have just written it for a job that is running here.
          const record = storage.load<BulkUploadRecord>(key)
          if (record && record.tabId !== TAB_ID && !self.isBusy) await restoreFrom(record, user)
        } finally {
          restoring = false
          maybeReleaseLock()
        }
      },

      /** A `storage` event on a record key, written by another tab in any channel: one job at a time (34). */
      async syncFromOtherTab(key: string) {
        const record = storage.load<BulkUploadRecord>(key)
        if (record?.running && record.tabId !== TAB_ID) {
          markOtherTab(true, record.job?.mode)
          return
        }
        // One channel's record going quiet says nothing about a job running in another channel.
        await recheckOtherTab(self.otherTabMode)
      },

      /** Logout, or anything else that ends the session on purpose: stop, and remove what's half-built. */
      async stopForExit() {
        if (!self.isBusy && self.unfinishedTaskIds.length === 0 && self.unfinishedSteps.length === 0) return
        // Unlocked even with Edit CM open on them: its own unlock on the way out comes after the session ends.
        const modules = new Set([...jobModules, ...self.unfinishedSteps.map(s => s.taskId)])
        abortAll()
        clearJob()
        const deadline = Date.now() + EXIT_SETTLE_MS
        while (self.inFlight > 0 && Date.now() < deadline) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
        await removeUnfinished({ confirm: false })
        letGoOfAllModules({ unlock: false })
        await Promise.all(
          [...modules].map(taskId => {
            lockHolders.delete(taskId)
            return unlock(taskId)
          }),
        )
        syncJobHeldModules()
        clearRecord()
      },

      /** Edit CM opened on a module; its lock is now shared with any job on it. */
      editorOpened(taskId: string) {
        holdModule(taskId, 'editor')
      },

      /** Edit CM closing. True when it should unlock, because no job is relying on the lock. */
      editorClosed(taskId: string): boolean {
        return releaseModule(taskId, 'editor')
      },

      stopForChannelChange,

      reset() {
        abortAll()
        clearInterval(progressTimer)
        clearTimeout(refreshTimer)
        progressTimer = refreshTimer = undefined
        pendingProgress.clear()
        files.clear()
        owner = undefined
        self._setJob(undefined)
        self._setUnfinished([])
        self._setUnfinishedSteps([])
        self._setActiveMode(undefined)
        window.removeEventListener('pagehide', unlockOnPageHide)
        jobModules.clear()
        moduleInFlight.clear()
        committing.clear()
        lockHolders.clear()
        self._setJobHeldModules([])
        self._clearCreating()
        stepsToRefresh.clear()
        listToRefresh = false
        markOtherTab(false)
        self._setRestorePending(false)
        cleaning = restoring = false
        lockRelease?.()
        lockRelease = undefined
      },
    }
  })

export interface BulkUploadStore extends Instance<typeof BulkUploadStoreModel> {}
